import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Sparkles, ShieldCheck, Clock, FileText } from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";
import ExitIntent from "../components/ExitIntent";
import PremiumBackdrop from "../components/PremiumBackdrop";

// Organic cubic-bezier from Scene 1 — the site-wide "wave" easing.
const EASE_ORGANIC = [0.2, 0.8, 0.2, 1];

// Fade-up preset for containers and static blocks.
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.9, delay, ease: EASE_ORGANIC },
});

/* -------------------------------------------------------------- */
/*  Wave — word-by-word reveal that matches Scene 1's rhythm      */
/*  Split on whitespace, animate each word: y 8→0, opacity 0→1,   */
/*  blur 6px→0, duration 0.42s, per-word stagger 0.05s.           */
/* -------------------------------------------------------------- */
function Wave({ text, className = "", delay = 0, goldWords = [] }) {
  const reduced = useReducedMotion();
  if (reduced) return <span className={className}>{text}</span>;
  const words = (text || "").split(/(\s+)/);
  const goldSet = new Set(goldWords);
  let idx = -1;
  return (
    <span className={className} aria-label={text}>
      {words.map((w, i) => {
        if (!w.trim()) return <React.Fragment key={i}>{w}</React.Fragment>;
        idx += 1;
        const clean = w.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
        const isGold = goldSet.has(clean);
        return (
          <motion.span
            key={i}
            aria-hidden
            className="inline-block will-change-transform"
            initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{
              duration: 0.42,
              ease: EASE_ORGANIC,
              delay: delay + idx * 0.05,
            }}
            style={isGold ? { color: "#C9A961" } : undefined}
          >
            {w}
          </motion.span>
        );
      })}
    </span>
  );
}

export default function Diagnose() {
  usePageTitle("Diagnose");
  return (
    <main
      className="min-h-screen pt-28 md:pt-36 pb-24 relative overflow-hidden"
      data-testid="diagnose-chooser"
      style={{ background: "#000000" }}
    >
      <PremiumBackdrop />
      <ExitIntent page="/diagnose" testid="diagnose-exit-intent" />
      {/* Champagne radial atmospherics — mirrors the homepage hero glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-16 h-[620px] w-[620px] rounded-full opacity-[0.14] blur-[120px]"
        style={{
          background:
            "radial-gradient(circle, rgba(201,169,97,0.9) 0%, rgba(201,169,97,0) 70%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 bottom-0 h-[520px] w-[520px] rounded-full opacity-[0.10] blur-[110px]"
        style={{
          background:
            "radial-gradient(circle, rgba(245,220,160,0.75) 0%, rgba(245,220,160,0) 70%)",
        }}
      />
      {/* Ambient technical grid — matches the chrome of Scenes 4-10 */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(201,169,97,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.35) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      {/* Top vignette so nav sits on a soft fade */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-64 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)",
        }}
      />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        {/* Kicker */}
        <motion.p
          {...fade(0)}
          className="font-sans text-[11px] uppercase tracking-[0.5em] mb-6 flex items-center gap-3"
          style={{ color: "#C9A961" }}
          data-testid="diagnose-kicker"
        >
          <span className="inline-block h-px w-8" style={{ background: "rgba(201,169,97,0.6)" }} />
          Choose your diagnosis
        </motion.p>

        {/* Heroic headline */}
        <h1
          className="max-w-4xl"
          data-testid="diagnose-hero-title"
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "clamp(1.9rem, 6vw, 5.2rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.02em",
            fontWeight: 500,
            color: "#F5F1E8",
          }}
        >
          <Wave text="See where your business" />
          <br />
          <span style={{ fontStyle: "italic" }}>
            <Wave text="is leaking growth." delay={0.35} goldWords={["growth"]} />
          </span>
        </h1>

        {/* Sub removed per founder request */}

        {/* Choice cards */}
        <div className="mt-16 md:mt-20 grid md:grid-cols-2 gap-6 md:gap-8">
          {/* FREE */}
          <motion.div
            {...fade(0.55)}
            className="group relative rounded-3xl p-8 md:p-10 overflow-hidden backdrop-blur-sm"
            data-testid="diagnose-card-free"
            style={{
              background: "rgba(0,0,0,0.6)",
              border: "1px solid rgba(201,169,97,0.4)",
              boxShadow:
                "inset 0 0 40px rgba(201,169,97,0.06), 0 0 32px rgba(201,169,97,0.08)",
            }}
          >
            <div
              aria-hidden
              className="absolute inset-x-0 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(201,169,97,0.65) 50%, transparent 100%)",
              }}
            />
            <p
              className="font-sans text-[11px] uppercase tracking-[0.4em]"
              style={{ color: "#C9A961" }}
            >
              Free · Six honest questions
            </p>
            <h2
              className="mt-4"
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(1.6rem, 4.4vw, 3.4rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.015em",
                fontWeight: 500,
                color: "#F5F1E8",
              }}
            >
              A quick, honest
              <br />
              <span style={{ fontStyle: "italic", color: "#C9A961" }}>diagnosis.</span>
            </h2>
            <p
              className="mt-6"
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(1rem, 1.4vw, 1.2rem)",
                lineHeight: 1.55,
                color: "rgba(245,241,232,0.75)",
              }}
            >
              Answer six questions. See where your business stands today. We then send
              you a short note from Krishna within 48 hours. No auto reports,
              no upsell, no strings.
            </p>

            <ul className="mt-8 space-y-3 font-sans text-base" style={{ color: "#F5F1E8" }}>
              <li className="flex items-start gap-3">
                <Sparkles className="h-4 w-4 mt-1" style={{ color: "#C9A961" }} />
                <span>Instant on-screen growth-health score</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-1" style={{ color: "#C9A961" }} />
                <span>Written response from Krishna within 48 hours</span>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck className="h-4 w-4 mt-1" style={{ color: "#C9A961" }} />
                <span>No credit card. No spam. No sales script.</span>
              </li>
            </ul>

            <Link
              to="/diagnosis"
              data-testid="diagnose-cta-free"
              className="mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm transition-colors"
              style={{
                border: "1.5px solid #C9A961",
                color: "#C9A961",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                background: "rgba(0,0,0,0.4)",
              }}
            >
              Start the free diagnosis
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* PRO */}
          <motion.div
            {...fade(0.7)}
            className="group relative rounded-3xl p-8 md:p-10 overflow-hidden backdrop-blur-sm"
            data-testid="diagnose-card-pro"
            style={{
              background:
                "linear-gradient(180deg, rgba(20,15,7,0.85) 0%, rgba(0,0,0,0.75) 100%)",
              border: "1px solid rgba(201,169,97,0.55)",
              boxShadow:
                "inset 0 0 60px rgba(201,169,97,0.14), 0 0 48px rgba(201,169,97,0.18)",
            }}
          >
            <div
              aria-hidden
              className="absolute inset-x-0 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, #C9A961 50%, transparent 100%)",
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -right-24 -bottom-24 h-72 w-72 rounded-full opacity-40 blur-3xl"
              style={{
                background:
                  "radial-gradient(circle, rgba(201,169,97,0.6) 0%, rgba(201,169,97,0) 70%)",
              }}
            />

            <p
              className="font-sans text-[11px] uppercase tracking-[0.4em]"
              style={{ color: "#C9A961" }}
            >
              Pro · $499 · One-time
            </p>
            <h2
              className="mt-4"
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(1.6rem, 4.4vw, 3.4rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.015em",
                fontWeight: 500,
                color: "#F5F1E8",
              }}
            >
              A written
              <br />
              <span style={{ fontStyle: "italic", color: "#C9A961" }}>diagnosis pro.</span>
            </h2>
            <p
              className="mt-6"
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(1rem, 1.4vw, 1.2rem)",
                lineHeight: 1.55,
                color: "rgba(245,241,232,0.75)",
              }}
            >
              Eleven deeper questions. Krishna researches your business and
              writes you an eight-page report in plain prose, no template. Delivered
              within 48 hours.
            </p>

            <ul className="mt-8 space-y-3 font-sans text-base" style={{ color: "#F5F1E8" }}>
              <li className="flex items-start gap-3">
                <FileText className="h-4 w-4 mt-1" style={{ color: "#C9A961" }} />
                <span>Eight-page written report, tailored to your business</span>
              </li>
              <li className="flex items-start gap-3">
                <Sparkles className="h-4 w-4 mt-1" style={{ color: "#C9A961" }} />
                <span>The single biggest bottleneck, named plainly</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-1" style={{ color: "#C9A961" }} />
                <span>Delivered to your inbox within 48 hours</span>
              </li>
            </ul>

            <Link
              to="/diagnosis-pro"
              data-testid="diagnose-cta-pro"
              className="mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full px-7 py-3.5 font-sans text-sm transition-colors"
              style={{
                background: "#C9A961",
                color: "#0A0A0A",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontWeight: 500,
                boxShadow: "0 0 32px rgba(201,169,97,0.35)",
              }}
            >
              Start Diagnosis Pro
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>

        {/* Closing line + signature removed per founder request */}
      </div>
    </main>
  );
}